import {
    ContentPropertyData,
    ControllableElement,
    CreativeTypes,
    FontFamily,
    TextStyle,
} from '@bynder-studio/render-core';
import { PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import { useSelector } from 'react-redux';
import { fontsElementsSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import { Account, ActionStatus, ColorPaletteAction } from 'packages/hooks/useAccessRights/types';
import { syncBrandColors } from 'packages/pages/editor/syncBrandColors';
import { colorPalettes } from 'packages/store/brandColors/brandColors.selectors';
import useAccessRights from 'packages/hooks/useAccessRights';
import { Component } from 'packages/services/componentService';
import { prepareDataForSave } from 'packages/common/editor/editor.bloc';
import useEditor from 'packages/hooks/useEditor';
import { exportElementsWithStyles, parseAndApplyElements } from './utils';

export interface PreparedComponent {
    elements: Record<string, unknown>[];
    textStyles?: TextStyle[];
    contentProperties: ContentPropertyData[];
    type: CreativeTypes;
}

const useComponents = () => {
    const { palettes } = useSelector(colorPalettes);
    const fonts: FontFamily[] = useSelector(fontsElementsSelector);
    const { creativeModel, manipulationRenderer, creativeType } = useEditor();
    const { isAccountActionAllowed } = useAccessRights();

    const createComponent = (selectedElements: ControllableElement[]): PreparedComponent => {
        if (manipulationRenderer instanceof PlaybackManipulationRenderer) {
            manipulationRenderer.pausePlayback();
        }

        const {
            elements: syncedSpecElements,
            textStyles: stylesList,
            contentProperties,
        } = exportElementsWithStyles(creativeModel, selectedElements);

        const { elements } = prepareDataForSave(false, syncedSpecElements, {}, 0, creativeType);

        const baseComponent = {
            elements: (elements || []) as Record<string, unknown>[],
            contentProperties,
            type: creativeType as unknown as CreativeTypes,
        };

        if (stylesList.length > 0) {
            return {
                ...baseComponent,
                textStyles: stylesList,
            };
        }

        return baseComponent;
    };

    const applyComponent = (preset: Component, isUniqueContentProperties = false) => {
        const startFrame =
            manipulationRenderer instanceof PlaybackManipulationRenderer ? manipulationRenderer.getCurrentFrame() : 0;

        parseAndApplyElements(creativeModel, preset as any, fonts, {
            startFrame,
            isUniqueContentProperties,
        });

        if (
            isAccountActionAllowed(ColorPaletteAction.EDIT_COLOR_PALETTE, Account.COLOR_PALETTES) ===
            ActionStatus.ALLOWED
        ) {
            syncBrandColors(creativeModel, palettes, () => null);
        }
    };

    return {
        applyComponent,
        createComponent,
    };
};

export default useComponents;
